import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { LANGUAGE_PAGES } from '../../data/languagePages';
import HomePage from '../Landing/HomePage';
import { useAskExamples } from '../Landing/useAskExamples';

const COPY_LOADERS = {
  es: () => import('../../data/homeCopy/es.json'),
  hi: () => import('../../data/homeCopy/hi.json'),
  zh: () => import('../../data/homeCopy/zh.json'),
  ar: () => import('../../data/homeCopy/ar.json'),
  fr: () => import('../../data/homeCopy/fr.json'),
  pt: () => import('../../data/homeCopy/pt.json'),
  de: () => import('../../data/homeCopy/de.json'),
  ja: () => import('../../data/homeCopy/ja.json'),
  ko: () => import('../../data/homeCopy/ko.json'),
  it: () => import('../../data/homeCopy/it.json'),
  tr: () => import('../../data/homeCopy/tr.json'),
  id: () => import('../../data/homeCopy/id.json'),
  pl: () => import('../../data/homeCopy/pl.json'),
  vi: () => import('../../data/homeCopy/vi.json'),
  th: () => import('../../data/homeCopy/th.json'),
  nl: () => import('../../data/homeCopy/nl.json'),
  ru: () => import('../../data/homeCopy/ru.json'),
  fil: () => import('../../data/homeCopy/fil.json'),
  uk: () => import('../../data/homeCopy/uk.json'),
  ro: () => import('../../data/homeCopy/ro.json'),
  sv: () => import('../../data/homeCopy/sv.json'),
  cs: () => import('../../data/homeCopy/cs.json'),
  el: () => import('../../data/homeCopy/el.json'),
  hu: () => import('../../data/homeCopy/hu.json'),
};

const LANGUAGE_STORAGE_KEY = 'nowtarot_preferred_language';

const pages = {};

function translatedHome(language) {
  if (!pages[language]) {
    pages[language] = lazy(() => COPY_LOADERS[language]().then((mod) => {
      const copy = mod.default || mod;
      const TranslatedHome = (props) => <HomePage copy={copy} {...props} />;
      return { default: TranslatedHome };
    }));
  }
  return pages[language];
}

function headFor(page, language) {
  const url = `https://nowtarot.com/${language}/`;
  return [
    <title key="title">{page.title}</title>,
    <meta key="description" name="description" content={page.description} />,
    <link key="canonical" rel="canonical" href={url} />,
    <meta key="og:title" property="og:title" content={page.title} />,
    <meta key="og:description" property="og:description" content={page.description} />,
    <meta key="og:url" property="og:url" content={url} />,
    <meta key="og:type" property="og:type" content="website" />,
    <meta key="og:site_name" property="og:site_name" content="NowTarot AI" />,
    <meta key="og:image" property="og:image" content="https://nowtarot.com/og-image.png" />,
    <meta key="twitter:card" name="twitter:card" content="summary_large_image" />,
  ];
}

const LanguageLanding = ({ language: languageProp }) => {
  const { language: languageParam } = useParams();
  const language = languageProp || languageParam;
  const page = LANGUAGE_PAGES[language];

  useEffect(() => {
    if (page?.lang) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, page.lang);
    }
  }, [page]);

  const examples = useAskExamples(page?.lang, page?.examples);
  const ask = useMemo(() => page && {
    language: page.lang,
    examples,
    typewriter: page.examples,
    askLabel: page.button,
    drawingLabel: page.loading,
  }, [page, examples]);

  if (!page || !COPY_LOADERS[language]) {
    return <Navigate to="/" replace />;
  }

  const TranslatedHome = translatedHome(language);
  return (
    <Suspense fallback={null}>
      <TranslatedHome
        lang={page.lang}
        dir={page.dir}
        head={headFor(page, language)}
        ask={ask}
      />
    </Suspense>
  );
};

export default LanguageLanding;
